import { getApiTable } from "./qmsApiUtils";

export const VAULT_FOLDER_ALL = "all";

/** ReportTable field metadata — columns returned by GetMyVaultDocuments */
export const VAULT_FIELD_OPTIONS = [
  { key: "DocumentId", label: "ID" },
  { key: "DocumentTitle", label: "Document" },
  { key: "DocumentStage", label: "Stage" },
  { key: "SystemDateTime", label: "Modified" },
  { key: "IsCheckOut", label: "Checked out" },
  { key: "CheckOutBy", label: "Checked out by" },
  { key: "DocumentType", label: "Type" },
  { key: "DocumentCategoryAbberiviation", label: "Category" },
  { key: "VersionNumber", label: "Version" },
  { key: "CreatedBy", label: "Owner" },
];

export const DEFAULT_VAULT_COLUMNS = [
  "DocumentId",
  "DocumentTitle",
  "DocumentStage",
  "SystemDateTime",
  "IsCheckOut",
  "CheckOutBy",
];

export function buildVaultFolderKey(typeAbbr, categoryAbbr) {
  if (!typeAbbr && !categoryAbbr) return VAULT_FOLDER_ALL;
  if (typeAbbr && !categoryAbbr) return `type:${typeAbbr}`;
  return `type:${typeAbbr}|cat:${categoryAbbr}`;
}

export function parseVaultFolderKey(folderKey) {
  if (!folderKey || folderKey === VAULT_FOLDER_ALL) {
    return { typeAbbr: "", categoryAbbr: "" };
  }
  const typeMatch = folderKey.match(/^type:([^|]+)/);
  const catMatch = folderKey.match(/\|cat:(.+)$/);
  return {
    typeAbbr: typeMatch ? typeMatch[1] : "",
    categoryAbbr: catMatch ? catMatch[1] : "",
  };
}

export function getVaultDocumentListId(row) {
  const typeAbbr = row?.DocumentTypeAbberiviation || "";
  const catAbbr =
    row?.DocumentCategoryAbberiviation || row?.DocumentCategoryAbbreviation || "";
  const uniqueNum = row?.DocumentCategoryUniqueNumber ?? "";
  const version = row?.VersionNumber ?? row?.versionNumber ?? "";
  if (!typeAbbr && !catAbbr && uniqueNum === "" && version === "") {
    return row?.DocumentId || "—";
  }
  return `${typeAbbr}_${catAbbr}_${uniqueNum}_V${version}`;
}

function escapeVaultHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function getVaultDocumentIdHelpText(row) {
  const typeAbbr = row?.DocumentTypeAbberiviation || "—";
  const catAbbr =
    row?.DocumentCategoryAbberiviation || row?.DocumentCategoryAbbreviation || "—";
  const uniqueNum = row?.DocumentCategoryUniqueNumber ?? "—";
  const version = row?.VersionNumber ?? row?.versionNumber ?? "—";
  const typeName = row?.DocumentType || typeAbbr;
  const categoryName = row?.DocumentCategory || catAbbr;

  return [
    "Document ID = Type_Category_Number_VVersion",
    `This document: ${typeAbbr}_${catAbbr}_${uniqueNum}_V${version}`,
    `Type: ${typeName} (${typeAbbr})`,
    `Category: ${categoryName} (${catAbbr})`,
    `Number: ${uniqueNum}`,
    `Version: ${version}`,
  ].join(" · ");
}

export function getVaultDocumentIdHelpHtml(row) {
  const typeAbbr = row?.DocumentTypeAbberiviation || "—";
  const catAbbr =
    row?.DocumentCategoryAbberiviation || row?.DocumentCategoryAbbreviation || "—";
  const uniqueNum = row?.DocumentCategoryUniqueNumber ?? "—";
  const version = row?.VersionNumber ?? row?.versionNumber ?? "—";
  const typeName = row?.DocumentType || typeAbbr;
  const categoryName = row?.DocumentCategory || catAbbr;

  return [
    '<div class="qms-vault-workspace__details-id-pop">',
    "<p><strong>Document ID format</strong></p>",
    "<p><code>Type_Category_Number_VVersion</code></p>",
    '<ul class="mb-0 ps-3">',
    `<li><strong>Type:</strong> ${escapeVaultHtml(typeName)} (${escapeVaultHtml(typeAbbr)})</li>`,
    `<li><strong>Category:</strong> ${escapeVaultHtml(categoryName)} (${escapeVaultHtml(catAbbr)})</li>`,
    `<li><strong>Number:</strong> ${escapeVaultHtml(String(uniqueNum))}</li>`,
    `<li><strong>Version:</strong> ${escapeVaultHtml(String(version))}</li>`,
    "</ul>",
    "</div>",
  ].join("");
}

export function getVaultDocumentIdAnchorHtml(row) {
  const documentId = getVaultDocumentListId(row);
  return `<span class="qms-vault-workspace__details-id-badge">${escapeVaultHtml(documentId)}</span>`;
}

/** True when a Details panel value should be rendered (non-empty, not placeholder). */
export function hasVaultDetailValue(value) {
  const text = String(value ?? "").trim();
  return Boolean(text) && text !== "—" && text !== "-" && text !== "N/A";
}

function getVaultLastActivity(response, info) {
  const logs = getApiTable(response, "DocumentLog");
  const fallbackAt = String(info?.SystemDateTime || "").trim() || "—";
  const fallbackBy = String(info?.CreatedBy || "").trim() || "—";

  if (Array.isArray(logs) && logs.length > 0) {
    const latest = logs[0] || {};
    const at = String(latest.ActionDateTime || latest.actionDateTime || "").trim();
    const by = String(latest.ActionBy || latest.actionBy || "").trim();
    return {
      at: at || fallbackAt,
      by: by || fallbackBy,
    };
  }

  return { at: fallbackAt, by: fallbackBy };
}

export function getVaultDocumentDisplayName(row) {
  const title = String(row?.DocumentTitle || "").trim();
  const ext = String(row?.DocumentExtension || "").replace(/^\./, "");
  if (!title && ext) return `Untitled.${ext}`;
  if (!title) return ext ? `Document.${ext}` : "Document";
  if (ext && !title.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
    return `${title}.${ext}`;
  }
  return title;
}

/** Base document name without extension (used for check-in API). */
export function getVaultDocumentTitle(row) {
  let title = getVaultDocumentDisplayName(row);
  const ext = String(row?.DocumentExtension || "").replace(/^\./, "");
  if (ext && title.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) {
    title = title.slice(0, -(ext.length + 1));
  }
  return title || "Document";
}

/** Parse category unique number field (supports "CAT-123" or "123"). */
export function parseVaultCategoryUniqueNumber(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const dashIdx = raw.indexOf("-");
  if (dashIdx > 0) return raw.slice(dashIdx + 1).trim();
  return raw;
}

export function getDefaultVaultUploadTitle(fileName) {
  const base = String(fileName || "").trim();
  if (!base) return "";
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(0, dot) : base;
}

export function buildVaultFolderTree(documents) {
  const typeMap = new Map();

  (documents || []).forEach((doc) => {
    const typeAbbr = doc.DocumentTypeAbberiviation || doc.DocumentType || "Other";
    const typeLabel = doc.DocumentType || typeAbbr;
    const catAbbr = doc.DocumentCategoryAbberiviation || doc.DocumentCategoryAbbreviation || "—";
    const catLabel = catAbbr;

    if (!typeMap.has(typeAbbr)) {
      typeMap.set(typeAbbr, { key: buildVaultFolderKey(typeAbbr, ""), label: typeLabel, count: 0, categories: new Map() });
    }
    const typeNode = typeMap.get(typeAbbr);
    typeNode.count += 1;

    if (!typeNode.categories.has(catAbbr)) {
      typeNode.categories.set(catAbbr, {
        key: buildVaultFolderKey(typeAbbr, catAbbr),
        label: catLabel,
        count: 0,
      });
    }
    typeNode.categories.get(catAbbr).count += 1;
  });

  const types = Array.from(typeMap.values())
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((typeNode) => ({
      ...typeNode,
      categories: Array.from(typeNode.categories.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    }));

  return {
    totalCount: (documents || []).length,
    types,
  };
}

export function filterVaultDocuments(documents, folderKey) {
  if (!folderKey || folderKey === VAULT_FOLDER_ALL) return documents || [];
  const { typeAbbr, categoryAbbr } = parseVaultFolderKey(folderKey);
  return (documents || []).filter((doc) => {
    const docType = doc.DocumentTypeAbberiviation || doc.DocumentType || "";
    const docCat = doc.DocumentCategoryAbberiviation || doc.DocumentCategoryAbbreviation || "";
    if (typeAbbr && docType !== typeAbbr) return false;
    if (categoryAbbr && docCat !== categoryAbbr) return false;
    return true;
  });
}

/** Documents with no workflow stage, or stage "New", are treated as Draft. */
export function resolveVaultDocumentStage(stage) {
  const value = String(stage ?? "").trim();
  if (!value || value.toLowerCase() === "new") return "Draft";
  return value;
}

export function getVaultStageClass(stage) {
  const value = String(resolveVaultDocumentStage(stage) || "").toLowerCase();
  if (value.includes("publish")) return "is-published";
  if (value.includes("approv")) return "is-approval";
  if (value.includes("review")) return "is-review";
  if (value.includes("draft")) return "is-draft";
  if (value === "new") return "is-new";
  return "is-default";
}

export function formatVaultYesNoExport(value) {
  const yes = Number(value) === 1 || value === true;
  return yes ? "Yes" : "No";
}

export function getVaultTypeDisplay(row, nameByAbbr) {
  const abbr = row?.DocumentTypeAbberiviation || "";
  const name = row?.DocumentType || nameByAbbr?.get(abbr) || "";
  return abbr && name ? `${abbr} - ${name}` : name || abbr || "—";
}

export function getVaultCategoryDisplay(row, nameByAbbr) {
  const abbr =
    row?.DocumentCategoryAbberiviation || row?.DocumentCategoryAbbreviation || "";
  const name = row?.DocumentCategory || nameByAbbr?.get(abbr) || "";
  return abbr && name ? `${abbr} - ${name}` : name || abbr || "—";
}

export function getVaultExportCellValue(row, field, lookups = {}) {
  const { typeNameByAbbr, categoryNameByAbbr } = lookups;
  if (field === "DocumentId") return getVaultDocumentListId(row);
  if (field === "DocumentTitle") return getVaultDocumentDisplayName(row);
  if (field === "DocumentType") return getVaultTypeDisplay(row, typeNameByAbbr);
  if (field === "DocumentCategoryAbberiviation") {
    return getVaultCategoryDisplay(row, categoryNameByAbbr);
  }
  if (field === "VersionNumber") {
    return row.VersionNumber ?? row.versionNumber ?? "";
  }
  if (field === "DocumentStage") return resolveVaultDocumentStage(row.DocumentStage);
  if (field === "SystemDateTime") return String(row.SystemDateTime || "");
  if (field === "CreatedBy") return String(row.CreatedBy || "");
  if (field === "IsCheckOut") return formatVaultYesNoExport(row.IsCheckOut);
  if (field === "CheckOutBy") return String(row.CheckOutBy || "");
  return String(row[field] ?? "");
}

/** Resolve active checkout reason from GetMyVaultDocumentByDocumentId response. */
export function resolveVaultCheckoutReason(response) {
  const records = getApiTable(response, "CheckOutRecords");
  const activeRecord =
    records.find((record) => Number(record.CheckOutStatus) === 1) || records[0] || null;

  const recordReason = String(
    activeRecord?.Comments || activeRecord?.CheckOutComments || "",
  ).trim();

  const logs = getApiTable(response, "DocumentLog");
  const checkoutLog = logs.find((entry) => {
    const action = String(entry.ActionName || "").toLowerCase();
    return action.includes("checked out") && !action.includes("cancel");
  });

  const logReason = String(checkoutLog?.Comments || "").trim();

  return {
    reason: recordReason || logReason || "",
    checkedOutBy:
      activeRecord?.CheckOutBy ||
      checkoutLog?.ActionBy ||
      "",
    checkedOutAt:
      activeRecord?.CheckOutDateTime ||
      checkoutLog?.ActionDateTime ||
      "",
  };
}

/** Build workspace Details panel fields from GetMyVaultDocumentByDocumentId. */
export function getVaultWorkspaceDocumentDetails(response, listRow = null) {
  const info = getApiTable(response, "DocumentInfo")[0] || listRow || {};
  const checkout = getApiTable(response, "CheckOutRecords")[0] || {};
  const checkoutMeta = resolveVaultCheckoutReason(response);
  const isCheckedOut =
    Number(checkout?.CheckOutStatus ?? info?.IsCheckOut ?? listRow?.IsCheckOut) === 1;

  const categoryLabel = [info.DocumentCategoryAbberiviation, info.DocumentCategory]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" — ");

  const lastActivity = getVaultLastActivity(response, info);
  const checkedOutBy = isCheckedOut
    ? checkoutMeta.checkedOutBy || checkout.CheckOutBy || info.CheckOutBy || ""
    : "";
  const checkedOutOn = isCheckedOut
    ? checkoutMeta.checkedOutAt || checkout.CheckOutDateTime || ""
    : "";
  const checkoutReason = isCheckedOut ? checkoutMeta.reason || "" : "";

  return {
    documentId: getVaultDocumentListId(info),
    documentIdHelp: getVaultDocumentIdHelpText(info),
    documentIdHelpHtml: getVaultDocumentIdHelpHtml(info),
    title: getVaultDocumentTitle(info),
    displayTitle: getVaultDocumentDisplayName(info),
    type: info.DocumentType || "—",
    category: categoryLabel || info.DocumentCategoryAbberiviation || "—",
    version: info.VersionNumber ?? info.versionNumber ?? "—",
    stage: info.DocumentStage || "—",
    modified: info.SystemDateTime || "—",
    modifiedBy: lastActivity.by,
    lastActivity: lastActivity.at,
    lastActivityBy: lastActivity.by,
    owner: info.CreatedBy || "—",
    reviewDate: info.ReviewDate || "—",
    supersededBy: info.SupersededBy || "—",
    supersededOn: info.SupersededDateTime || "—",
    isCheckedOut,
    checkedOutBy,
    checkedOutOn,
    checkoutReason,
    showCheckoutDetails:
      isCheckedOut &&
      (hasVaultDetailValue(checkedOutBy) ||
        hasVaultDetailValue(checkedOutOn) ||
        hasVaultDetailValue(checkoutReason)),
  };
}

export function isGuidLike(value) {
  return /^[0-9a-f-]{36}$/i.test(String(value || "").trim());
}

export function buildStaffByIdMap(staffRows) {
  const map = new Map();
  (Array.isArray(staffRows) ? staffRows : []).forEach((staff) => {
    const id = String(staff?.StaffId || "").trim().toLowerCase();
    if (id) map.set(id, staff);
  });
  return map;
}

/** Resolve a staff GUID to display name; pass-through if already a name. */
export function resolveStaffDisplay(staffById, value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (isGuidLike(raw)) {
    return staffById.get(raw.toLowerCase())?.StaffName || raw;
  }
  return raw;
}

export const parseTags = (tags) =>
  String(tags || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

export const joinTags = (tags) => {
  const list = Array.isArray(tags) ? tags : parseTags(tags);
  return list.join(",");
};
